import { useQuery } from '@tanstack/react-query';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { notificationTypesService } from '../../services/notification-types.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import ActiveTag from '../../components/common/active-tag';
import ChannelIcons from '../../components/common/channel-icons';
import {
  CARD, BackLink, CardHeader, Field, Hero, RecordMeta,
} from '../../components/notifications/engine-view-ui';

const NotificationTypeView = () => {
  const { id, isReady, navigateToList, navigateToEdit } = useSecureNavigation({ entity: 'notification type', basePath: '/notification-types' });
  const canUpdate = useCanUpdate(JOB_NAMES.NOTIFICATION_TYPE);
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['notificationType', id], queryFn: () => notificationTypesService.getById(id!), enabled: isReady && !!id });

  if (isLoading) return <PageSkeleton />;
  if (isError || !data) return (
    <div className="p-4">
      <Message severity="error" text={extractErrorMessage(error, 'Notification type not found')} className="mb-4 w-full" />
      <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigateToList()} />
    </div>
  );

  const editable = canUpdate && !data.isDelete;
  const retry = data.retryPolicy?.backoff === 'FIXED'
    ? `Every ${data.retryPolicy?.intervalSeconds ?? 60}s, until delivered`
    : `${data.retryPolicy?.intervalSeconds ?? 60}s, doubling, until delivered`;

  return (
    <div className="mx-auto space-y-3 p-4" data-testid="SCR-NotificationType-View">
      <BackLink label="Notification Types" onClick={() => navigateToList()} />

      <Hero
        icon="tags"
        chip="bg-indigo-50 text-indigo-500 dark:bg-indigo-950/50 dark:text-indigo-400"
        code={data.notificationCode}
        tags={<>
          <Tag value={data.category} severity="secondary" className="text-xs px-1.5 py-0.5" />
          {data.priority && <Tag value={data.priority} severity="info" className="text-xs px-1.5 py-0.5" />}
          <ActiveTag row={data} />
        </>}
        description={data.description}
        actions={editable && <Button label="Edit" icon="pi pi-pencil" size="small" severity="secondary" onClick={() => id && navigateToEdit(id)} />}
      />

      <div className={CARD}>
        <CardHeader icon="send" color="text-emerald-400" title="Channels" />
        <div className="px-4 py-4">
          <ChannelIcons channels={data.channels} variant="chips" empty="None — nothing can be delivered." />
        </div>
      </div>

      <div className={CARD}>
        <CardHeader icon="settings" color="text-violet-400" title="Delivery policy" />
        <div className="grid grid-cols-1 gap-x-6 gap-y-3.5 px-4 py-4 sm:grid-cols-3">
          <Field label="Channel fallback">
            {data.isChannelFallbackEnabled ? 'Enabled' : 'Disabled'}
            {data.isChannelFallbackEnabled && <span className="block text-xs text-gray-400">A failed channel tries its backup.</span>}
          </Field>
          <Field label="SLA">
            {data.slaMinutes ? `${data.slaMinutes} minutes` : 'No deadline'}
            {data.slaMinutes ? <span className="block text-xs text-gray-400">From delivery; met when the recipient acts on any channel.</span> : null}
          </Field>
          <Field label="Expires after">{data.expiresAfterSeconds ? `${data.expiresAfterSeconds}s` : 'Never'}</Field>
          <Field label="Retention">{data.retentionDays != null ? `${data.retentionDays} days` : '—'}</Field>
          <Field label="Quiet hours">{data.isQuietHoursRespected !== false ? 'Respected' : 'Ignored'}</Field>
          <Field label="Opt-out">
            {data.isUserOptOutAllowed !== false ? 'Allowed' : 'Not allowed'}
            {data.isUserOptOutAllowed === false && <span className="block text-xs text-gray-400">Unopt-outable: a person&rsquo;s own choice is ignored for this class.</span>}
          </Field>
          <div className="sm:col-span-3">
            <Field label="Retry pacing">
              {retry}
              <span className="block text-xs text-gray-400">No attempt limit. Waits are capped at 6 hours.</span>
            </Field>
          </div>
        </div>
      </div>

      <RecordMeta data={data} extra={<>
        <Field label="Category">{data.category}</Field>
        <Field label="Priority">{data.priority || '—'}</Field>
      </>} />
    </div>
  );
};

export default NotificationTypeView;
